import cloneDeep from 'lodash/cloneDeep';
import type { ItemMetaData } from '../dto/fieldValue';
import type { FieldValueDiff, MetadataSnapshot } from '../dto/mdfModels';
import type { VueSet, VueDelete } from '../util/general';
import type { MdfSafeDiff } from './MetaDataForm';
import { getMdfDiff, hasChangesInMetadata } from './MetaDataForm';
import keyBy from 'lodash/keyBy';
import { invariant } from '../util/invariant';
import isEqual from 'lodash/isEqual';

export type MdfUpdateItem = {
  metadata: ItemMetaData,

  // fixme typing: It's not clear whether we always do have metadataVersion
  // available when invoking handleMdfUpdatesToItem
  metadataVersion?: number,

  metadataSnapshot?: MetadataSnapshot,
};

export const handleMdfUpdatesToItem = (
  item: MdfUpdateItem,
  newMetadata: ItemMetaData,
  newMetadataVersion: number,
  setter: VueSet,
  deleter: VueDelete
) => {

  if (!item.metadata) {
    return;
  }

  if (newMetadata === item.metadata) {
    // If item.metadata points to the same object as newMetadata
    // we have nothing to do, and it indicates the caller is using
    // this method wrongly.
    // The below code also assumes that this is not the case.
    console.log('handleMdfUpdatesToItem: current and new metadata is same object');
    return;
  }

  if (item.metadataVersion !== undefined && newMetadataVersion < item.metadataVersion) {
    console.log('handleMdfUpdatesToItem: Skipping metadata update, have newer version');
    return;
  }

  const verifiedMetadata = retrieveSafeMdfUpdates(newMetadata, item.metadata, item.metadataSnapshot?.latestSnapshot);
  if (verifiedMetadata === 'server_all') {
    setter({
      object: item.metadata,
      key: 'formId',
      value: newMetadata.formId
    });
    Object.keys(item.metadata.formData).forEach(key => deleter({ object: item.metadata!.formData, key }));
    for (const [key, value] of Object.entries(newMetadata.formData)) {
      setter({
        object: item.metadata.formData,
        key,
        value
      });
    }
    item.metadataSnapshot = {
      latestSnapshot: cloneDeep(item.metadata),
      metadataVersion: newMetadataVersion,
      conflictedFields: [],
    };
  } else {
    const metadataSnapshot = item.metadataSnapshot;
    invariant(metadataSnapshot, 'Must have metadataSnapshot since we got conflicting fields');
    for (const f of verifiedMetadata.safeFields) {
      // FIXME: If f.oldValue is undefined here, we will set the field value
      // to undefined, which is not a valid formData value.
      setter({
        object: item.metadata.formData,
        key: f.fieldId,
        value: f.oldValue
      });
      if (f.oldValue) {
        metadataSnapshot.latestSnapshot.formData[f.fieldId] = f.oldValue;
      }
    }

    metadataSnapshot.conflictedFields = verifiedMetadata.conflictedFields;
    metadataSnapshot.metadataVersion = newMetadataVersion;
  }
};


/**
 * Apply metadata changes in a more safe manner. We need to ensure that values locally changed by users are preserved, while any other fields
 * are updated for the best possible user experience.
 *
 * We also return the values that were _not_ overwritten, to be able to warn the user of changes made.
 *
 * @param serverMetadata The latest metadata state we've seen from the server
 * @param currentMetadata The current state of the edited metadata by the user
 * @param backupMetadata The previous metadata before the user started editing
 * @returns A diff containing safe vs conflicted fields, or the value 'server_all' to indicate the complete server state should be used
 */
const retrieveSafeMdfUpdates = (
  serverMetadata: ItemMetaData,
  currentMetadata: ItemMetaData,
  backupMetadata?: ItemMetaData
): MdfSafeDiff | 'server_all' => {

  if (!backupMetadata) {
    return 'server_all';
  }

  const changedByUserDiff = getMdfDiff(backupMetadata, currentMetadata);
  const safeFields: FieldValueDiff[] = [];
  const conflictedFields: FieldValueDiff[] = [];

  // If the user made no changes we can bail out early.
  if (!hasChangesInMetadata(changedByUserDiff)) {
    return 'server_all';
  }

  // Trying to diff new mdf id vs old mdf id is a complex matrix. If we detect a different incoming mdf id from the update,
  // do not attempt to figure out what field to keep. We consciously force refresh the entire metadata form, unfortunately causing
  // users to potentially lose their values. Corner case situation.
  if (serverMetadata.formId !== backupMetadata.formId && serverMetadata.formId !== changedByUserDiff.newMdfId) {
    // TODO: Should give the user some options of what to do here, probably
    // as a first step choosing which formId to use.
    return 'server_all';
  }

  const diff = getMdfDiff(serverMetadata, currentMetadata);
  if (!hasChangesInMetadata(diff)) {
    return 'server_all';
  }

  if (diff.newMdfId !== diff.oldMdfId && serverMetadata.formId !== diff.oldMdfId) {
    return 'server_all';
  }

  const mappedById = keyBy(changedByUserDiff.metadataFields, f => f.fieldId);
  for (const changedField of diff.metadataFields) {
    const fieldWasChanged = mappedById[changedField.fieldId] !== undefined;
    if (fieldWasChanged) {
      if (!isEqual(backupMetadata.formData[changedField.fieldId], changedField.oldValue)) {
        // We've changed a field that has changed to a different value on the server
        conflictedFields.push(changedField);
      }
    } else {
      safeFields.push(changedField);
    }
  }

  return {
    safeFields,
    conflictedFields
  };
};
